import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import OnboardingWizard from '../components/OnboardingWizard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_HOME = {
  platform_admin: '/dashboard',
  school_admin: '/dashboard',
  teacher: '/dashboard',
  parent: '/parent-dashboard',
  student: '/dashboard',
};

export default function FirstTimeSetupPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const t = (hi, en) => (language === 'hi' ? hi : en);

  const role = user?.role;
  const isSchoolAdmin = role === 'school_admin';
  const needsProfileWizard = isSchoolAdmin && user?.needs_initial_setup === true;
  // Step 1 = password reset (mandatory if temp_password_required), Step 2 = profile wizard (newly-provisioned school_admin only)
  const initialStep = user?.temp_password_required ? 1 : (needsProfileWizard ? 2 : 3);
  const [step, setStep] = useState(initialStep);

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState(null);

  const passwordIssues = (pw) => {
    const issues = [];
    if (pw.length < 8) issues.push(t('कम से कम 8 character', 'At least 8 chars'));
    if (!/[A-Z]/.test(pw)) issues.push(t('एक uppercase letter', 'One uppercase letter'));
    if (!/[a-z]/.test(pw)) issues.push(t('एक lowercase letter', 'One lowercase letter'));
    if (!/[0-9]/.test(pw)) issues.push(t('एक digit', 'One digit'));
    return issues;
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    if (pwForm.next !== pwForm.confirm) {
      setPwError(t('नया password match नहीं हो रहा', 'New passwords do not match'));
      return;
    }
    const issues = passwordIssues(pwForm.next);
    if (issues.length) {
      setPwError(t('Password requirements: ', 'Password requirements: ') + issues.join(', '));
      return;
    }
    setPwBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setPwError(data?.detail || data?.error || t('Update विफल', 'Update failed'));
        setPwBusy(false);
        return;
      }
      // Update local user state — clear flag
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        stored.temp_password_required = false;
        localStorage.setItem('user', JSON.stringify(stored));
      } catch { /* ignore */ }
      // Move to step 2 (newly-provisioned school admin) or finish
      if (needsProfileWizard) {
        setStep(2);
      } else {
        await markComplete();
      }
    } catch (err) {
      setPwError(err?.message || 'Error');
    } finally {
      setPwBusy(false);
    }
  };

  const markComplete = async () => {
    try {
      await fetch(`${API_URL}/api/auth/complete-first-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.first_setup_complete = true;
      stored.needs_initial_setup = false;
      localStorage.setItem('user', JSON.stringify(stored));
    } catch { /* non-fatal */ }
    navigate(ROLE_HOME[role] || '/dashboard', { replace: true });
  };

  const skipProfile = async () => {
    await markComplete();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center p-4" data-testid="first-time-setup-page">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-black text-slate-900 mb-2">
            {t('आपका Account Ready है', 'Your Account is Ready')}
          </h1>
          <p className="text-slate-600">
            {t(`नमस्ते ${user?.name || ''}! एक छोटा सा setup पूरा करें ताकि आप शुरुआत कर सकें।`,
               `Hi ${user?.name || ''}! Complete a quick setup to get started.`)}
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-6 bg-white rounded-xl p-3 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <Stepper active={step >= 1} done={step > 1} label={t('1. Password', '1. Password')} />
            {isSchoolAdmin && <>
              <div className="flex-1 h-1 bg-slate-100 rounded">
                <div className={`h-1 rounded transition-all ${step > 1 ? 'bg-orange-500 w-full' : 'w-0'}`} />
              </div>
              <Stepper active={step >= 2} done={step > 2} label={t('2. School Profile', '2. School Profile')} />
            </>}
            <div className="flex-1 h-1 bg-slate-100 rounded">
              <div className={`h-1 rounded transition-all ${step > 2 ? 'bg-orange-500 w-full' : 'w-0'}`} />
            </div>
            <Stepper active={step >= 3} done={step > 2} label={t('Done', 'Done')} />
          </div>
        </div>

        {/* Step 1: Password Reset */}
        {step === 1 && (
          <form onSubmit={submitPassword} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="step-password">
            <h2 className="text-xl font-bold text-slate-900 mb-1">🔒 {t('नया Password Set करें', 'Set Your New Password')}</h2>
            <p className="text-sm text-slate-500 mb-4">
              {t('Security के लिए, आपको temporary password बदलना ज़रूरी है।',
                 'For security, you must change the temporary password.')}
            </p>
            <Field label={t('Current (Temporary) Password', 'Current (Temporary) Password')}>
              <input
                type="password"
                value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                required
                data-testid="pw-current"
                className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500"
                autoComplete="current-password"
              />
            </Field>
            <Field label={t('New Password', 'New Password')}>
              <input
                type="password"
                value={pwForm.next}
                onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                required
                data-testid="pw-next"
                className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500"
                autoComplete="new-password"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t('Min 8 chars · 1 uppercase · 1 lowercase · 1 digit', 'Min 8 chars · 1 uppercase · 1 lowercase · 1 digit')}
              </p>
            </Field>
            <Field label={t('Confirm New Password', 'Confirm New Password')}>
              <input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                required
                data-testid="pw-confirm"
                className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500"
                autoComplete="new-password"
              />
            </Field>
            {pwError && (
              <div className="mt-3 p-2.5 rounded-lg bg-rose-50 text-rose-700 text-sm border border-rose-200" data-testid="pw-error">
                {pwError}
              </div>
            )}
            <button
              type="submit"
              disabled={pwBusy}
              data-testid="pw-submit"
              className="mt-5 w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg font-bold"
            >
              {pwBusy ? t('Update हो रहा...', 'Updating...') : t('Password Update करें', 'Update Password')}
            </button>
          </form>
        )}

        {/* Step 2: School Profile Wizard (school_admin only) */}
        {step === 2 && isSchoolAdmin && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="step-school-profile">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-xl font-bold text-slate-900">🏫 {t('स्कूल Profile Wizard', 'School Profile Wizard')}</h2>
                <p className="text-sm text-slate-500">
                  {t('यह wizard आपका school setup पूरा करने में help करेगा। आप बाद में भी बदल सकते हैं।',
                     'This wizard helps you complete your school setup. You can edit anytime later.')}
                </p>
              </div>
              <button
                onClick={skipProfile}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-orange-600 hover:underline"
                data-testid="skip-profile-btn"
              >
                {t('अभी Skip करें →', 'Skip for now →')}
              </button>
            </div>
            <OnboardingWizard
              isOpen={true}
              onClose={skipProfile}
              token={token}
              onSuccess={markComplete}
            />
          </div>
        )}

        {/* Step 3: All done */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-8 text-center" data-testid="step-done">
            <div className="text-6xl mb-3">🎉</div>
            <h2 className="text-2xl font-bold text-emerald-700 mb-2">{t('Setup पूरा हो गया!', 'Setup Complete!')}</h2>
            <p className="text-slate-600 mb-5">
              {t('Dashboard खुल रहा है...', 'Opening dashboard...')}
            </p>
            <button
              onClick={markComplete}
              data-testid="go-dashboard"
              className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold"
            >
              {t('Dashboard पर जाएं', 'Go to Dashboard')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ active, done, label }) {
  const cls = done
    ? 'bg-emerald-500 text-white border-emerald-500'
    : active
      ? 'bg-orange-500 text-white border-orange-500'
      : 'bg-white text-slate-400 border-slate-200';
  return (
    <div className={`px-3 py-1.5 rounded-full border text-xs font-bold ${cls} shrink-0`}>
      {done ? '✓' : ''} {label}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-sm font-semibold text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
